<template>
  <div name="planningedit" class="inner">
    <innertopbar @close="closeWindow()">
      <template v-slot:breadcrumb>
        <router-link to="events"> Long Term Planning </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>
        <li
          v-if="hasExtendedLayout"
          @click="deletePlan"
          v-tooltip="{
            content: 'Delete Plan',
            placement: 'bottom-center',
            offset: 5,
            classes: ['tooltip-info'],
          }"
          title="Delete Plan"
          class="action-list-item inline-block bg-list-grey hover:opacity-100"
        >
          <i class="icon fas fa-trash-alt" title="Delete Plan"></i>
        </li>

        <li v-if="hasExtendedLayout" class="inline-block mx-4 text-2xl text-gray-400">
          |
        </li>

        <li class="inline-block">
          <button
            v-if="!currentUser.isOnTrial"
            @click="submitForm()"
            type="button"
            :disabled="isDisabled"
            class="publish-button hover:bg-opacity-75"
          >
            Publish Changes
          </button>
          <button
            v-else
            type="button"
            :disabled="isDisabled"
            class="publish-button bg-gray-200 text-red-400 hover:bg-opacity-75"
          >
            Planning Locked
          </button>
        </li>
      </template>
    </innertopbar>

    <section v-if="errored" class="w-full text-center mt-6">
      <h3 class="font-bold text-xl text-action-red">
        There was an issue adding the planning details.
      </h3>
      <p class="text-action-blue">
        Please try again shortly or contact us to resolve the issue.
      </p>
    </section>

    <form
      v-on:submit.prevent="submitPlanning"
      id="planningForm"
      ref="form"
      v-if="!isPlanningComplete"
      class=""
    >
      <div class="w-full mx-auto flex gap-10">
        <div class="mt-6 ml-8 p-6 bg-white rounded-lg mb-10 w-7/12 h-70v">
          <h2 class="text-lg font-bold my-3">Changes to be Published</h2>
          <p
            v-if="validationInError"
            class="bg-red-400 rounded w-11/12 text-white p-4 mt-2"
          >
            <i class="fas fa-exclamation-circle pr-2 text-xl"></i>All required
            fields must be complete to add planning.
          </p>

          <div
            class="flex flex-wrap mb-4 p-3 rounded-lg"
            :class="hasUnit ? 'bg-green-500' : 'bg-red-400'"
          >
            <div class="w-full">
              <label for="grid-title" class="font-semibold text-white">
                Unit Selected:
              </label>
              <div class="font-bold text-base text-white">
                {{ unitTitle }}
              </div>
            </div>
          </div>

          <div class="flex flex-wrap px-3 mb-4">
            <div class="w-full">
              <label class="font-semibold" for="grid-title"> Term: </label>
              <div class="font-bold text-base text-gray-500">
                {{ term.name }}
              </div>
            </div>
          </div>

          <div class="flex flex-wrap px-3 mb-4">
            <div class="flex w-full">
              <div class="w-1/2">
                <label class="font-semibold" for="grid-title">
                  Year Group:
                </label>
                <div class="font-bold text-base text-gray-500">
                  {{ yearGroupTitle }}
                </div>
              </div>
              <div v-if="isOnSEN">
                <label class="font-semibold" for="grid-title">
                  SEN Override:
                </label>
                <Dropdown
                  :options="yearDropdown"
                  :selected="selectedYear"
                  :title="'Year Group'"
                  v-on:updateOption="setYearValue"
                  style="min-width: 12rem"
                ></Dropdown>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap px-3 mb-4">
            <div class="w-full">
              <label class="font-semibold" for="grid-title">
                Display Box:
              </label>
              <div class="font-bold text-base text-gray-500">
                {{ displayBoxTitle }}
              </div>
            </div>
          </div>

          <div
            v-if="hasExtendedLayout"
            class="grid space-y-1 pb-4 b grid-cols-1"
          >
            <div class="">
              <label class="font-semibold text-gray-600">Teacher:</label>

              <div class="mt-2 w-5/12 -ml-1 text-left">
                <Dropdown
                  :options="teacherDropdown"
                  :selected="teacherSelected"
                  v-on:updateOption="updateTeacher"
                  class="text-left px-0 bg-white mr-0"
                ></Dropdown>
              </div>
            </div>
          </div>

          <div v-if="hasExtendedLayout" class="grid grid-cols-1 pt-2 space-y-1">
            <div class="">
              <label class="font-semibold text-gray-600">School Rooms:</label>
              <p v-if="schoolRooms.length == 0">No rooms setup</p>
              <ul class="mt-2 mb-1">
                <li
                  v-for="item in schoolRooms"
                  :key="item.id"
                  class="inline-block mr-2"
                >
                  <button
                    type="button"
                    @click="selectRoom(item)"
                    class="filter-item-button hover:text-action-blue border-gray-300 border hover:bg-opacity-50 wid text-xs"
                    v-bind:class="
                      inSelectedRooms(item.identifier)
                        ? 'filter-item-button-on bg-gray-500 '
                        : 'filter-item-button-off'
                    "
                  >
                    <i
                      class="far pr-1 hover:text-action-blue"
                      v-bind:class="
                        inSelectedRooms(item.identifier)
                          ? 'fa-check-square'
                          : 'fa-square'
                      "
                    ></i>
                    {{ item.title }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="p-6 pb-0 pt-6 w-full">
          <div class="block h-54 bg-white p-4 mb-6 rounded-2xl overflow-y-auto">
            <div
              style="width: 100%; text-align: center"
              class="spin-holder mt-4"
              v-if="loading"
            >
              <i class="spinner fa fa-spinner fa-spin"></i>
              <p>Loading Units</p>
            </div>
            <div class="pl-2 flex content-center justify-between">
              <div class="font-semibold text-gray-600 pl-2">
                Select a unit of work from the types below.
              </div>
            </div>

            <div class="grid grid-cols-5 gap-2 w-full mx-auto mb-3">
              <div class="mt-3" v-for="path in tabsList" :key="path.id">
                <button
                  type="button"
                  class="text-sm font-bold focus:outline-none p-4 rounded-xl w-full"
                  @click="selectedTab = path"
                  v-bind:class="[
                    selectedTab.id === path.id
                      ? 'text-white bg-blue-500'
                      : 'text-gray-500 bg-gray-100',
                  ]"
                >
                  {{ path.name }}
                </button>
              </div>
            </div>
            </div>
              <div class="block h-screen bg-white p-4 rounded-2xl overflow-y-auto mb-10">

            <div
              v-if="componentLoaded"
              class="mb-2 max-h-screen overflow-y-auto pt-2"
            >
              <AdvertMaxW
                v-if="!hasMaxWhitlockAccess && isSelectedMaxW"
              ></AdvertMaxW>

              <table class="table-wrapper w-full h-full mx-auto mb-32 text-xs">
                <tbody>
                  <tr
                    class="course"
                    v-for="(item, index) in orderedUnits"
                    :key="item.id"
                  >
                    <td class="w-10/12">
                      <div class="pl-2 flex">
                        <div
                          class="w-2 mr-3"
                          v-bind:style="{
                            backgroundColor: getBackgroundColor(item),
                          }"
                        >
                          &nbsp;
                        </div>
                        <div>
                          <strong>{{ item.name }}</strong
                          ><br />
                          <span class="text-xs opacity-25 italic">
                            <strong>{{ item.category.name }}</strong></span
                          >
                        </div>
                      </div>
                    </td>

                    <td>
                      <button
                        v-if="shouldDisplay(index)"
                        type="button"
                        @click="addNewPlan(item)"
                        class="filter-item-button mt-2 text-xs border-gray-300 border hover:bg-opacity-50 wide"
                        v-bind:class="
                          isUnderSelection(item)
                            ? 'filter-item-button-on'
                            : 'filter-item-button-off'
                        "
                      >
                        Select
                      </button>

                      <i
                        v-else
                        @click="showUpgrade()"
                        class="fas fa-lock text-yellow-500 cursor-pointer text-lg py-2 pl-2"
                      ></i>
                    </td>
                  </tr>
                </tbody>
                <tfoot></tfoot>
              </table>
            </div>
            <section class="pb-20 mt-4 w-full overflow-y-auto max-h-screen">
              <AdvertConcept
                v-if="!hasConceptAccess && isSelectedConcept"
              ></AdvertConcept>
              <AdvertResourceBank
                v-else-if="!hasResourceBankAccess && isSelectedResourceBank"
              ></AdvertResourceBank>
              <AdvertMaxW
                v-else-if="
                  hasResults == false && !hasMaxWhitlockAccess && isSelectedMaxW
                "
              ></AdvertMaxW>
              <p
                v-else-if="hasResults == false"
                class="m-4 p-4 font-bold text-sm"
              >
                No units available for selected year, please select again
              </p>
            </section>
          </div>
        </div>
      </div>
    </form>

    <!------------ UPGRADE -->
    <UpgradeModal
      v-if="showUpgradeModal"
      @close="showUpgrade()"
      :area="'Pupils'"
    ></UpgradeModal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
const Dropdown = () => import("@/components/Dropdown.vue");
const innertopbar = () => import("@/components/InnerTopBar.vue");

const AdvertConcept = () => import("@/components/planning/adverts/Concept.vue");
const AdvertResourceBank = () =>
  import("@/components/planning/adverts/ResourceBank.vue");
const AdvertMaxW = () => import("@/components/planning/adverts/Maxw.vue");

const UpgradeModal = () => import("@/components/upgrade/UpgradeModal.vue");
const UpgradeOption = () => import("@/components/upgrade/UpgradeOption.vue");

import { SchoolService, BaseService } from "@/services/school.service";
import { calendarYearList, termList } from "@/assets/js/utils";
import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";
import { roomMixin } from "@/mixins/roomMixin.js";
import { upgradeMixin } from "@/mixins/upgradeMixin.js";

export default {
  name: "planningedit",
  mixins: [yearGroupMixin, roomMixin, upgradeMixin],
  props: {
    term: { type: Object, required: true },
    displayBox: { type: Number, required: true },
    year: { type: Number, required: true, default: 0 },
    selectedPlan: { type: Object, required: false },
  },
  watch: {
    selectedPlan(val, oldval) {
      this.selectedPlan = val;
      this.teacherSelected = undefined;
      this.selectedSchoolRooms = [];
      this.parseSelectedPlan();
    },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
      settings: "settings",
    }),
    showLancs() {
      return this.currentUser.schoolStyle == "lancashire";
    },
    isOnSEN() {
      return this.currentUser.isOnSEN;
    },
    isDisabled() {
      if (this.inEdit) {
        return false;
      }

      return this.unitSelected === undefined;
    },
    inEdit() {
      return this.selectedPlan != undefined;
    },
    hasUnit() {
      return this.unitSelected != undefined;
    },
    unitTitle() {
      return !this.hasUnit ? "No unit selected" : this.unitSelected.name;
    },
    yearGroupTitle() {
      let value = this.getYearGroupFromId(this.year);

      return value != undefined ? value.name : "unknown";
    },
    displayBoxTitle() {
      let result = (this.displayBox + "").split(".");
      return "Column: " + result[0] + ", Row: " + result[1];
    },
    componentLoaded() {
      return this.orderedUnits.length > 0;
    },
    hasResults() {
      return this.componentLoaded;
    },
    customUnits() {
      var vm = this;
      let type = vm.courseTypes.find((c) => c.key_value === "school");

      var filtered = vm.schoolUnits.filter(function (unit) {
        let filtered = true;

        filtered = unit.year_id == vm.year && unit.type_id === type.id;

        return filtered;
      });

      return _.orderBy(filtered, ["display_order", "name", "category_id"]);
    },
    orderedUnits() {
      var vm = this;

      let types = vm.courseTypes
        .filter((c) => {
          if (vm.selectedTab.path == "core") {
            return c.key_value === "free" || c.key_value === "organisation";
          } else {
            return c.key_value === vm.selectedTab.path;
          }
        })
        .map((obj) => obj.id);

      var filtered = vm.units.filter(function (unit) {
        let filtered = true;
        filtered = unit.year_id == vm.selectedYear.id;

        if (filtered && unit.type != undefined) {
          filtered = types.includes(unit.type.id);
        }

        return filtered;
      });

      let results = _.orderBy(filtered, [
        "display_order",
        "name",
        "category_id",
      ]);

      if (!this.hasMaxWhitlockAccess && this.isSelectedMaxW) {
        let filtered = results.filter(function (unit) {
          return unit.year_id === 2;
        });
        return filtered.length > 0 ? [results[0]] : [];
      } else {
        return results;
      }
    },
    title() {
      let vm = this;
      let year = this.years.find(function (y) {
        return y.id === vm.year;
      });
      return year ? year.name + " Planning Setup " : "";
    },
    teacherDropdown() {
      let results = [{ id: 0, name: "Select a teacher" }].concat(this.teachers);
      if (this.teacherSelected === undefined) {
        this.teacherSelected = results[0];
      }
      return results;
    },
    hasExtendedLayout() {
      return this.currentUser.isSecondaryBased;
    },
    tabsList() {
      return this.tabs;
    },
    hasConceptAccess() {
      return this.currentAccess.conceptAccess;
    },
    hasResourceBankAccess() {
      return this.currentAccess.peResourceBankAccess;
    },
    hasMaxWhitlockAccess() {
      return this.currentAccess.maxWhitlockAccess;
    },
    isSelectedConcept() {
      return this.selectedTab.id === 3;
    },
    isSelectedResourceBank() {
      return this.selectedTab.id === 6;
    },
    isSelectedMaxW() {
      return this.selectedTab.id === 8;
    },
    yearDropdown() {
      let results = [{ id: 0, name: "Select a year group" }].concat(this.years);
      if (this.selectedYear === undefined) {
        this.selectedYear = results[0];
      }
      return results;
    },
  },
  data: function () {
    return {
      terms: termList(),
      showModal: false,
      units: [],
      cats: [],
      teachers: [],
      courseTypes: [],
      isPlanningComplete: false,
      errored: false,
      validationInError: false,
      teacherSelected: undefined,
      unitSelected: undefined,
      selectedSchoolRooms: [],
      tabs: [],
      selectedTab: {},
      selectedYear: {},
    };
  },
  mounted() {
    this.tabs = [
      { id: 1, name: "Core PE", path: "core" },
      { id: 2, name: "School", path: "school" },
      { id: 3, name: "Concept", path: "concept" },
      { id: 4, name: "Dance", path: "d2s" },
      { id: 5, name: "Pippa & Eddie", path: "pippaeddie" },
      { id: 6, name: "PE Bank", path: "peresourcebank" },
      { id: 7, name: "Community", path: "community" },
      { id: 8, name: "Max Whitlock", path: "maxw" },
      { id: 9, name: "Swim England", path: "swimming" },
    ];

    if (this.showLancs === false && this.settings.isAllowedFree) {
      this.tabs.push({ id: 10, name: "Free Plans", path: "oakacademy" });
    }

    if (this.currentUser.isOnFree) {
          this.selectedTab = this.tabs[this.tabs.length - 1];
     } else {
        this.selectedTab = this.tabs[0];
     }
  
    this.selectedYear = this.getYearGroupFromId(this.year);
  },
  created() {
    if (this.term == undefined) {
      this.closeWindow();
      return;
    }

    // cats
    BaseService.getAll("categories")
      .then((response) => this.buildCategoryList(response.data.data))
      .catch((res) => {
        console.log(res);
      });
    BaseService.getAll("course-types")
      .then((response) => (this.courseTypes = response.data.data))
      .catch((res) => {
        console.log(res);
      });

    // users
    SchoolService.getAll("users")
      .then((response) => (this.teachers = response.data.data))
      .catch((err) => {
        console.log("users " + err);
        this.errored = true;
      });

    this.fetchCourseData();
    this.parseSelectedPlan();
  },
  methods: {
    fetchCourseData() {
      let freeParam = this.settings.courseDisplayType == "custom" ? "" : "free";
      let conceptParam = this.hasConceptAccess ? "concept," : "";
      let resourceBankParam = this.hasResourceBankAccess
        ? "peresourcebank,"
        : "";

      SchoolService.getAll(
        "courses?visibility=member&state=open&types=pippaeddie,d2s,maxw,oakacademy,swimming," +
          conceptParam +
          resourceBankParam +
          freeParam
      )
        .then((response) => this.buildUnitList(response.data.data))
        .catch((err) => {
          console.log("Courses " + err);
          this.errored = true;
        });
    },
    buildUnitList(data) {
      this.units = data;
    },
    buildCategoryList(data) {
      this.cats = data;
    },
    parseSelectedPlan() {
      if (this.selectedPlan != undefined) {
        if (this.selectedPlan.user != undefined) {
          this.updateTeacher(this.selectedPlan.user);
        }
        if (this.selectedPlan.rooms != undefined) {
          this.selectedPlan.rooms.forEach((element) => {
            this.selectRoom(element);
          });
        }
      }
    },
    hasTerm() {
      return false;
    },
    updateTeacher(value) {
      this.teacherSelected = value;
    },
    inSelectedRooms(identifier) {
      var found = this.selectedSchoolRooms.find(
        (y) => y.identifier === identifier
      );
      return found === undefined ? false : true;
    },
    selectRoom(room) {
      if (this.inSelectedRooms(room.identifier)) {
        this.selectedSchoolRooms.splice(
          this.selectedSchoolRooms.findIndex(
            (item) => item.identifier === room.identifier
          ),
          1
        );
      } else {
        this.selectedSchoolRooms.push(room);
      }
    },
    addNamePlanWithCheck(unit) {
      if (this.isAllowed(unit)) {
        this.addNewPlan(unit);
      }
    },

    submitForm() {
      if (this.$refs.form.requestSubmit === undefined) {
        this.submitPlanning();
      } else {
        this.$refs.form.requestSubmit();
      }
    },
    submitPlanning() {
      if (this.inEdit) {
        this.editPlanning();
      } else {
        if (this.unitSelected === undefined) {
          this.validationInError = true;
        } else {
          this.processPlanning();
        }
      }
    },
    isUnderSelection(unit) {
      if (this.unitSelected === undefined) {
        return false;
      }

      return this.unitSelected.id === unit.id;
    },
    addNewPlan(unit) {
      this.unitSelected = unit;
    },
    editPlanning() {
      let teacherId =
        this.teacherSelected != undefined && this.teacherSelected.id > 0
          ? this.teacherSelected.id
          : null;

      var object = this.selectedPlan;
      object.user_id = teacherId;
      object.rooms = this.selectedSchoolRooms;

      SchoolService.update("planning", object.identifier, object)
        .then((response) => {
          this.planningAddition(response.data);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    processPlanning() {
      let calendarYear = calendarYearList().find((y) => y.current === true);

      let teacherId =
        this.teacherSelected != undefined && this.teacherSelected.id > 0
          ? this.teacherSelected.id
          : null;
      let payload = {
        //term: this.term.term_key,
        calendar_schoolyear: calendarYear.value,
        term: this.term.key,
        course_id: this.unitSelected.id,
        user_id: teacherId,
        display_box: this.displayBox,
        rooms: this.selectedSchoolRooms,
      };

      SchoolService.create("planning", payload)
        .then((response) => {
          this.planningAddition(response.data);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    planningAddition(data) {
      // let identifier = data != undefined ? data.identifier : "";
      this.unitSelected = undefined;
      this.closeWindow();
    },
    isAllowed(unit) {
      let vm = this;
      let type = vm.courseTypes.find((c) => c.id === unit.type_id);
      return type != undefined && vm.currentAccess.hasModule(type.key_value);
    },
    shouldDisplay(index) {
      if (index === 0 && this.currentUser.isOnTrial) {
        return true;
      } else if (this.currentUser.isOnTrial) {
        return false;
      } else {
        if (this.currentUser.isOnFree) {
          switch (this.selectedTab.path) {
            case "core":
              return this.currentAccess.planningAccess;
            case "d2s":
              return this.currentAccess.danceAccess;
            case "pippaeddie":
              return this.currentAccess.pippaEddieAccess;
            default:
              return true;
          }
        } else if (this.currentUser.isOnLite) {
          switch (this.selectedTab.path) {
            case "d2s":
              return this.currentAccess.danceAccess;
            case "pippaeddie":
              return this.currentAccess.pippaEddieAccess;
            default:
              return true;
          }
        } else {
          return true;
        }
      }
    },
    getBackgroundColor(unit) {
      if (unit != undefined) {
        return "#" + unit.category.color_hex;
      }
    },
    openInNewTab(url) {
      window.open(url, "_blank");
    },
    deletePlan() {
      if (this.selectedPlan === undefined) {
        return;
      }
      let vm = this;
      var object = vm.selectedPlan;

      let payload = {
        object: object,
        year: vm.year,
      };

      SchoolService.delete("planning", object.identifier, null).then(
        (response) => {
          vm.$store.commit("DELETE_PLANNING_UNIT", payload);
          vm.planningAddition();
        }
      );
    },
    closeWindow() {
      this.$router.push("/planning");
    },
    setYearValue(value) {
      this.selectedYear = value;
    },
  },
  components: {
    Dropdown,
    innertopbar,
    AdvertConcept,
    AdvertResourceBank,
    AdvertMaxW,
    UpgradeModal,
    UpgradeOption,
  },
};
</script>

<style scoped>
.slideinsecond {
  min-width: 100%;
  position: fixed;
  z-index: 100;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0);
  height: 100%;
}

.slidesecond-enter-active {
  transition: all 0.85s ease-out;
}

.slidesecond-leave-active {
  transition: all 0.45s ease-out;
}
.slide-econd-enter-to {
  right: 0;
}
.slidesecond-enter-from {
  right: -100%;
}
.slidesecond-leave-to {
  right: -100%;
}
.slidesecond-leave-from {
  right: 0;
}

.slidesecond-leave-active,
.slidesecond-enter-active {
  transition: 0.4s ease;
}

.slidesecond-enter {
  transform: translate(100%, 0);
}
.slidesecond-leave-to {
  transform: translate(-100%, 0);
}

.sidesecond {
  z-index: 101;
  position: absolute;
  top: 0;
  right: 0;
  background: #fff;
  height: 100%;
  opacity: 1 !important;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.3);
}

table td {
  padding-top: 0.05em;
  padding-bottom: 0.05em;
}

ul {
  padding-left: 1em;
  padding-top: 4px;
  padding-bottom: 4px;
}

li {
  list-style-type: circle !important;
  font-weight: bold;
  padding-bottom: 4px;
}

.link {
  @apply inline-flex items-center py-2 text-sm font-semibold text-center text-action-blue cursor-pointer;
}

.selected {
  @apply text-blue-500 font-bold border-b border-blue-500;
}
</style>
